// utils/socketManager.js
import io from "socket.io-client";

class SocketManager {
  constructor() {
    this.socket = null;
    this.eventListeners = new Map();
    this.isConnecting = false;
    this.heartbeatInterval = null;
    this.connectionCheckInterval = null;
    this.lastPongTime = Date.now();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.baseReconnectDelay = 1000;
  }

  connect(options = {}) {
    if (this.socket?.connected || this.isConnecting) {
      return this.socket;
    }

    this.isConnecting = true;

    const defaultOptions = {
      transports: ["websocket", "polling"], // Websocket first, polling as fallback
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      pingInterval: 10000,
      pingTimeout: 20000,
      closeOnBeforeunload: false,
      secure: process.env.NODE_ENV === "production",
      timeout: 10000,
      forceNew: false,
      autoConnect: false,
      // Polling specific options
      pollingTimeout: 30000, // 30 seconds polling timeout
      upgrade: true, // Allow upgrading from polling to websocket
      rememberUpgrade: true, // Remember the upgrade for future connections
    };

    this.socket = io(process.env.REACT_APP_SOCKET_URL, {
      ...defaultOptions,
      ...options,
    });

    this.setupEventHandlers();
    // Attach any listeners registered before the socket existed (components
    // can mount and call on() before connect() runs).
    this.reattachListeners();
    this.socket.connect();
    this.addVisibilityHandler();
    this.startHeartbeat();
    this.startConnectionCheck();
    this.isConnecting = false;

    return this.socket;
  }

  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("Socket connected:", this.socket.id);
      console.log("Transport:", this.socket.io.engine.transport.name);
      this.reconnectAttempts = 0;
      this.lastPongTime = Date.now();
      this.reattachListeners();
      // "connect" also fires on every reconnect (the socket never emits "reconnect" in v4).
      // Without a restart the heartbeat stayed off after the first drop, so the health
      // check saw no pong and forced a reconnect every ~2 minutes, losing events each time.
      this.startHeartbeat();
      this.startConnectionCheck();

      // Log transport upgrades
      this.socket.io.engine.on("upgrade", (transport) => {
        console.log("Transport upgraded to:", transport.name);
      });
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      console.log("Last transport:", this.socket?.io?.engine?.transport?.name);
      this.stopHeartbeat();
      this.stopConnectionCheck();

      // Only a server-side disconnect stops socket.io's own reconnection (Infinity attempts).
      // Reconnecting manually on network drops as well ran a second loop that tore down
      // its attempts and doubled the reconnect load on the socket server after a deploy.
      if (reason === "io server disconnect") {
        this.scheduleReconnect();
      }
    });

    this.socket.on("reconnect_attempt", (attempt) => {
      console.log(`Reconnect attempt #${attempt}`);
    });

    this.socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      console.log("Failed transport:", this.socket?.io?.engine?.transport?.name);
    });

    // Handle custom heartbeat responses
    this.socket.on("pong", (data) => {
      this.lastPongTime = Date.now();
      console.log("Received pong, connection alive");
    });

    // Handle transport errors
    this.socket.io.on("error", (error) => {
      console.error("Socket.IO error:", error);
    });
  }

  startHeartbeat() {
    this.stopHeartbeat();
    
    // Different heartbeat intervals based on transport
    const getHeartbeatInterval = () => {
      const transport = this.socket?.io?.engine?.transport?.name;
      if (transport === "polling") {
        return 20000; // 20 seconds for polling (more frequent)
      } else {
        return 25000; // 25 seconds for websocket
      }
    };
    
    // Send ping every interval
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        const transport = this.socket.io.engine.transport.name;
        console.log(`Sending heartbeat ping via ${transport}`);
        this.socket.emit("ping", { 
          timestamp: Date.now(),
          transport: transport 
        });
      }
    }, getHeartbeatInterval());
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  startConnectionCheck() {
    this.stopConnectionCheck();
    
    // Check connection health every 30 seconds
    this.connectionCheckInterval = setInterval(() => {
      // Hidden tabs throttle timers to about once a minute, so a late pong there is not a
      // dead connection; socket.io's own ping timeout still catches real drops
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      const timeSinceLastPong = now - this.lastPongTime;
      const transport = this.socket?.io?.engine?.transport?.name;
      
      // Different timeout thresholds based on transport
      const getTimeoutThreshold = () => {
        if (transport === "polling") {
          return 90000; // 90 seconds for polling (more lenient)
        } else {
          return 60000; // 60 seconds for websocket
        }
      };
      
      // If no pong response in threshold time, consider connection stale
      if (timeSinceLastPong > getTimeoutThreshold()) {
        console.log(`Connection appears stale (${transport}), forcing reconnect`);
        console.log(`Time since last pong: ${timeSinceLastPong}ms`);
        this.forceReconnect();
      }
    }, 30000);
  }

  stopConnectionCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log("Max reconnection attempts reached");
      return;
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );
    
    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
    
    setTimeout(() => {
      if (!this.socket?.connected) {
        this.reconnectAttempts++;
        this.forceReconnect();
      }
    }, delay);
  }

  forceReconnect() {
    console.log("Forcing socket reconnection");
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    }
  }

  reattachListeners() {
    // Reattach all registered listeners after reconnection
    this.eventListeners.forEach((callbacks, eventName) => {
      callbacks.forEach((callback) => {
        this.socket?.off(eventName, callback);
        this.socket?.on(eventName, callback);
      });
    });
  }

  on(eventName, callback) {
    // Always record the listener, even before the socket exists — components
    // mounted before connect() (e.g. the always-mounted Sidebar) register here
    // and reattachListeners() flushes the map once the socket connects.
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, new Set());
    }

    this.eventListeners.get(eventName).add(callback);

    if (this.socket) {
      this.socket.on(eventName, callback);
    }
  }

  off(eventName, callback) {
    this.socket?.off(eventName, callback);

    if (this.eventListeners.has(eventName)) {
      this.eventListeners.get(eventName).delete(callback);
      if (this.eventListeners.get(eventName).size === 0) {
        this.eventListeners.delete(eventName);
      }
    }
  }

  emit(eventName, data, callback) {
    if (!this.socket?.connected) {
      console.warn(`Cannot emit ${eventName}: Socket not connected`);
      return false;
    }

    if (callback) {
      this.socket.emit(eventName, data, callback);
    } else {
      this.socket.emit(eventName, data);
    }
    return true;
  }

  disconnect() {
    if (this.socket) {
      // Clean up intervals
      this.stopHeartbeat();
      this.stopConnectionCheck();
      
      // Keep eventListeners: mounted components own their registrations and
      // remove them via off() on unmount; a later connect() re-attaches the
      // rest. Clearing here would permanently deafen still-mounted components.
      this.removeVisibilityHandler();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  getSocketId() {
    return this.socket?.id || null;
  }

  addVisibilityHandler() {
    const handler = () => {
      if (document.visibilityState === "visible") {
        console.log("Tab is active. Verifying socket...");
        
        // Reset reconnection attempts when tab becomes visible
        this.reconnectAttempts = 0;
        
        if (!this.isConnected()) {
          console.log("Socket was disconnected. Reconnecting...");
          this.forceReconnect();
        } else {
          // Even if connected, restart heartbeat to ensure health
          this.startHeartbeat();
          this.startConnectionCheck();
        }
      } else {
        console.log("Tab is hidden. Keeping connection alive with heartbeat.");
        // Don't disconnect on hide, just rely on heartbeat
      }
    };
    
    document.addEventListener("visibilitychange", handler);
    this._visibilityHandler = handler;
  }

  removeVisibilityHandler() {
    if (this._visibilityHandler) {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }

  // Get connection health info
  getConnectionHealth() {
    const transport = this.socket?.io?.engine?.transport?.name;
    return {
      connected: this.isConnected(),
      transport: transport,
      lastPongTime: this.lastPongTime,
      timeSinceLastPong: Date.now() - this.lastPongTime,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.getSocketId(),
      engineId: this.socket?.io?.engine?.id,
      upgraded: this.socket?.io?.engine?.upgraded || false
    };
  }

  // Force transport downgrade if having issues
  forceTransportDowngrade() {
    if (this.socket?.io?.engine?.transport?.name === "websocket") {
      console.log("Forcing transport downgrade to polling");
      this.socket.io.engine.upgrade = false;
      this.forceReconnect();
    }
  }

  // Try to upgrade transport
  tryTransportUpgrade() {
    if (this.socket?.io?.engine?.transport?.name === "polling") {
      console.log("Attempting transport upgrade to websocket");
      this.socket.io.engine.upgrade = true;
    }
  }
}

// Export singleton instance
export const socketManager = new SocketManager();
